import React, { useState, useEffect } from "react";
import axios from "axios";
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx'; // Add XLSX to handle Excel export
import './Users.css';
import API_URL from '../../api/ExamCalendarApi';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    password: "",
  });
  const [searchTerm, setSearchTerm] = useState(""); // Search term state
  const [filter, setFilter] = useState("all"); // Filter state (all, admin, user)

  const getAuthToken = () => localStorage.getItem("authToken");

  // Fetch all users
  const fetchUsers = async () => {
    setLoading(true);
    setError("");
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API_URL}/user/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data.users);
    } catch (error) {
      setError("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleInputChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const addUser = async () => {
    setError("");
    setSuccess("");
    const { name, mobile, email, password } = formData;
    if ( !name || !mobile || !email || !password) {
      setError("All fields are required");
      return;
    }

    setLoading(true);
    try {
      const token = getAuthToken();
      await axios.post(
        `${API_URL}/user/admin/create`,
        { name, mobile, email, password },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSuccess("User created successfully");
      setShowAddModal(false);
      fetchUsers();
    } catch (error) {
      setError("Failed to add user");
    } finally {
      setLoading(false);
    }
  };

  const toggleAdmin = async (userId) => {
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const token = getAuthToken();
      await axios.put(`${API_URL}/user/admin/toggle-admin/${userId}`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess("User admin status updated successfully");
      fetchUsers();
    } catch (error) {
      setError("Failed to update user admin status");
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId) => {
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const token = getAuthToken();
      await axios.delete(`${API_URL}/user/admin/delete/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess("User deleted successfully");
      fetchUsers();
    } catch (error) {
      setError("Failed to delete user");
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on search term and filter type (admin/user)
  const filteredUsers = users.filter((user) => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.studentId.toLowerCase().includes(searchTerm.toLowerCase()); // Include studentId in the search
    const matchesFilter =
      filter === "all" ||
      (filter === "admin" && user.isAdmin) ||
      (filter === "user" && !user.isAdmin);

    return matchesSearch && matchesFilter;
  });

  // Export to Excel
  const exportToExcel = () => {
    const adminUsers = filteredUsers.filter((user) => user.isAdmin);
    const normalUsers = filteredUsers.filter((user) => !user.isAdmin);

    const adminData = adminUsers.map((user) => ({
      "Student ID": user.studentId,
      Name: user.name,
      Email: user.email,
      Mobile: user.mobile,
      "Is Admin": user.isAdmin ? "Yes" : "No",
      "Is Subscribed": user.isSubscribed ? "Yes" : "No",
    }));

    const userData = normalUsers.map((user) => ({
      "Student ID": user.studentId,
      Name: user.name,
      Email: user.email,
      Mobile: user.mobile,
      "Is Admin": user.isAdmin ? "Yes" : "No",
      "Is Subscribed": user.isSubscribed ? "Yes" : "No",
    }));

    const wb = XLSX.utils.book_new();
    if (adminData.length > 0) {
      const adminWS = XLSX.utils.json_to_sheet(adminData);
      XLSX.utils.book_append_sheet(wb, adminWS, "Admin Users");
    }
    if (userData.length > 0) {
      const userWS = XLSX.utils.json_to_sheet(userData);
      XLSX.utils.book_append_sheet(wb, userWS, "Normal Users");
    }

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), "filtered_users.xlsx");
  };

  return (
    <div className="admin-users-container">
      <div className="admin-users-section">
        <h1>User Management</h1>

        {loading && <div className="admin-users-spinner"></div>}
        {error && <div className="admin-users-alert admin-users-alert-danger">{error}</div>}
        {success && <div className="admin-users-alert admin-users-alert-success">{success}</div>}

        {/* Search Bar and Filter Options */}
        <div className="admin-users-input-group">
          <input
            className="admin-users-form-control"
            placeholder="Search by name or student ID"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="admin-users-select"
          >
            <option value="all">All</option>
            <option value="admin">Admins</option>
            <option value="user">Users</option>
          </select>
        </div>

        <button
          className="admin-users-btn admin-users-btn-success"
          onClick={() => setShowAddModal(true)}
        >
          Add New User
        </button>
        <div className="admin-users-table-wrapper">
        <table className="admin-users-table">
          <thead>
            <tr>
              <th>Student ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Mobile</th>
              <th>Is Admin</th>
              <th>Is Subscribed</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user._id}>
                <td>{user.studentId}</td> {/* Display Student ID */}
                <td>{user.name}</td>
                <td>{user.email}</td>
                <td>{user.mobile}</td>
                <td>{user.isAdmin ? "Yes" : "No"}</td>
                <td>{user.isSubscribed ? "Yes" : "No"}</td>
                <td>
                  <button
                    className={`admin-users-btn ${user.isAdmin ? "admin-users-btn-warning" : "admin-users-btn-primary"}`}
                    onClick={() => toggleAdmin(user._id)}
                  >
                    {user.isAdmin ? "Remove Admin" : "Make Admin"}
                  </button>
                  <button
                    className="admin-users-btn admin-users-btn-danger"
                    onClick={() => deleteUser(user._id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        <button className="admin-users-btn admin-users-btn-info" onClick={exportToExcel}>
          Download Data as Excel
        </button>

        {/* Add User Modal */}
        <div className={`admin-users-modal ${showAddModal ? "admin-users-modal-show" : ""}`}>
          <div className="admin-users-modal-content">
            <div className="admin-users-modal-header">
              <button className="admin-users-close" onClick={() => setShowAddModal(false)}>&times;</button>
              <h5 className="admin-users-modal-title">Add New User</h5>
            </div>
            <div className="admin-users-modal-body">
              {/* <div className="admin-users-form-group">
                <label>Student ID</label>
                <input
                  type="text"
                  name="studentId"
                  value={formData.studentId}
                  onChange={handleInputChange}
                  className="admin-users-form-control"
                />
              </div> */}
              <div className="admin-users-form-group">
                <label>Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="admin-users-form-control"
                />
              </div>
              <div className="admin-users-form-group">
                <label>Mobile</label>
                <input
                  type="text"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  className="admin-users-form-control"
                />
              </div>
              <div className="admin-users-form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="admin-users-form-control"
                />
              </div>
              <div className="admin-users-form-group">
                <label>Password</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="admin-users-form-control"
                />
              </div>
            </div>
            <div className="admin-users-modal-footer">
              <button className="admin-users-btn admin-users-btn-secondary" onClick={() => setShowAddModal(false)}>
                Close
              </button>
              <button className="admin-users-btn admin-users-btn-success" onClick={addUser}>
                Add User
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Users;
